import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  connectorApi,
  type ConnectorItem,
  type ConnectorType,
  CONNECTOR_TYPES,
} from '../api/integrationApi'

export default function ConnectorEditPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [type, setType] = useState<ConnectorType>('api')
  const [config, setConfig] = useState('{}')
  const [isActive, setIsActive] = useState(true)
  const [connector, setConnector] = useState<ConnectorItem | null>(null)

  useEffect(() => {
    if (!id) return
    const load = async () => {
      setLoading(true)
      try {
        const data = await connectorApi.getById(Number(id))
        setConnector(data)
        setName(data.name)
        setDescription(data.description || '')
        setType(data.type)
        setConfig(data.config || '{}')
        setIsActive(data.isActive)
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : t('integrations.errors.loadFailed'))
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id, t])

  const handleSave = async () => {
    if (!name.trim()) return
    setSaving(true)
    setError(null)
    try {
      await connectorApi.update(Number(id), {
        name: name.trim(),
        description: description.trim() || undefined,
        type,
        config,
        isActive,
      })
      navigate('/system/integrations')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('integrations.errors.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const handleTest = async () => {
    try {
      await connectorApi.test(Number(id))
      const data = await connectorApi.getById(Number(id))
      setConnector(data)
    } catch {
      setError(t('integrations.errors.testFailed'))
    }
  }

  const inputCls = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
  const labelCls = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-[900px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('integrations.actions.edit')} — {connector?.name}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {connector?.createdBy && `${t('integrations.columns.connector')} #${id}`}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleTest}
            className="px-4 py-2 text-sm bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
          >
            {t('integrations.actions.test')}
          </button>
          <button
            onClick={() => navigate('/system/integrations')}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {t('integrations.actions.cancel')}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {connector?.lastTestedStatus && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${
          connector.lastTestedStatus === 'success'
            ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400'
            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
        }`}>
          {t(`integrations.status.${connector.lastTestedStatus}`)} — {connector.lastTestedAt ? new Date(connector.lastTestedAt).toLocaleString() : ''}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
        <div>
          <label className={labelCls}>{t('integrations.fields.connectorName')} *</label>
          <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        <div>
          <label className={labelCls}>{t('integrations.fields.description')}</label>
          <textarea className={inputCls} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>

        <div>
          <label className={labelCls}>{t('integrations.columns.type')}</label>
          <select className={inputCls} value={type} onChange={(e) => setType(e.target.value as ConnectorType)}>
            {CONNECTOR_TYPES.map((ct) => (
              <option key={ct.value} value={ct.value}>{t(ct.labelKey)}</option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelCls}>Config (JSON)</label>
          <textarea
            className={`${inputCls} font-mono text-xs`}
            rows={8}
            value={config}
            onChange={(e) => setConfig(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="isActive"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600"
          />
          <label htmlFor="isActive" className="text-sm text-gray-700 dark:text-gray-300">
            {t('integrations.columns.active')}
          </label>
        </div>
      </div>

      <div className="flex justify-end mt-6">
        <button
          onClick={handleSave}
          disabled={saving || !name.trim()}
          className="px-6 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {saving ? t('integrations.actions.saving') : t('integrations.actions.edit')}
        </button>
      </div>
    </div>
  )
}
